import styled from '@emotion/styled';
import { useAppDispatch } from '@/redux/hooks';
import { LabelValue } from '@/components/_common';
import { Button, Grid, lighten, Tooltip } from '@mui/material';
import { Link, useHistory } from 'react-router-dom';
import UserDisplay from '@/components/_common/AssigneeSelect/UserDisplay';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';
import FileType from '@/components/_common/Icons/FileType';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { Order } from '@/redux/slices/slice_types';
import { setCurrentTable, clearFilters, applyFilter } from '@/redux/slices/dataTableSlice';
import StatusDisplay from '@/components/_common/StatusDisplay';
import { useConfigValue } from '@/components/_hooks/useConfigValue';

interface BulkModalMetadataProps {
  order: Order;
  showCancelButton?: boolean;
  onCancel: () => void;
}

const BulkModalMetadata = ({ order, showCancelButton = false, onCancel }: BulkModalMetadataProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const bulkOrderMetadata = useConfigValue(['orderPlacementConfig', 'bulkOrderMetadata']);

  const onViewAllJobsClick = () => {
    dispatch(setCurrentTable('jobs'));
    dispatch(clearFilters(undefined));
    const orderIdFilter = { type: 'filter_id', selections: [order.hid, 'order_hid_filter'] };
    dispatch(applyFilter(orderIdFilter));
    history.push(`/jobs`);
  };

  const renderMetadata = () => {
    if (bulkOrderMetadata) {
      return bulkOrderMetadata(order);
    } else {
      const fileType = order?.draft_state?.file?.name?.split('.').pop();
      return (
        <>
          <LabelValue label="Order ID">{order.hid}</LabelValue>
          <LabelValue label="Description">
            <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <BulkIcon icon="bulk" height={15} width={15} />
              {order.description}
            </span>
          </LabelValue>
          <LabelValue label="Order Number">{order.po_number}</LabelValue>
          <LabelValue label="Managed by">
            <UserDisplay user={order?.managed_by_user} />
          </LabelValue>
          <LabelValue label="Client Name">{order.billable_customer}</LabelValue>
          <LabelValue label="Budget Code">{order.budget_code}</LabelValue>
          <LabelValue label="Billing Code">{order.billing_code}</LabelValue>
          <LabelValue label="Created">
            <DateTimeDisplay singleLine>{order?.created_at}</DateTimeDisplay>
          </LabelValue>
          <LabelValue label="Customer">{order.draft_state?.profileData?.customer}</LabelValue>
          <LabelValue label="Profile">
            <StyledLink to={`/profiles/${order.draft_state?.profileData?.id}`} target="_blank">
              {order.draft_state?.profileData.label}
            </StyledLink>
          </LabelValue>
          {fileType && (
            <LabelValue label="Imported File Name">
              <Grid container alignItems="center">
                <FileType type={fileType} height={20} style={{ marginRight: 5 }} />
                {order?.draft_state?.file?.name}
              </Grid>
            </LabelValue>
          )}
        </>
      );
    }
  };
  return (
    <MetadataBar container item sx={{ mb: 4 }}>
      <Grid item container rowSpacing={2} sx={{ width: 'calc(100% - 270px)' }}>
        {renderMetadata()}
      </Grid>
      <Grid item>
        {showCancelButton ? (
          order.status.includes('cancel') ? (
            <StatusDisplay status={order.status} reason={order.status_reason} />
          ) : (
            <Grid container item alignItems="center">
              <Button onClick={onCancel} variant="outlined" color="default" sx={{ ml: 'auto', whiteSpace: 'nowrap', mr: 1 }}>
                Cancel Order
              </Button>
              <Tooltip
                title="The import data did not generate any jobs."
                arrow
                slotProps={{ tooltip: { sx: { maxWidth: 180, textAlign: 'center' } } }}
              >
                <PolarisIcon icon="info" style={{ opacity: 0.6 }} />
              </Tooltip>
            </Grid>
          )
        ) : (
          <Button
            onClick={onViewAllJobsClick}
            variant="outlined"
            sx={{ ml: 'auto', whiteSpace: 'nowrap', minWidth: 125 }}
            endIcon={<PolarisIcon icon="right_arrow" />}
          >
            View All Jobs
          </Button>
        )}
      </Grid>
    </MetadataBar>
  );
};

export default BulkModalMetadata;

const MetadataBar = styled(Grid)`
  background: ${({ theme }) => (theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.03) : theme.palette.grey[100])};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;

  > div {
    display: flex;
    flex-direction: row;
    column-gap: ${({ theme }) => theme.spacing(5)};
    padding: ${({ theme }) => theme.spacing(1.75, 2.5, 2, 3)};
    border-radius: 5px;
    align-items: center;
  }

  > .MuiButtonBase-root {
    margin-top: 0px;
    height: 40px;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
`;

const BulkIcon = styled(PolarisIcon)`
  margin-right: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.secondary.main};
`;
